import * as Api from '@ViewModels';
import { QueryClient, QueryKey } from '@tanstack/react-query';
import { ServerSentEventMessage } from 'fetch-event-stream';
import { Draft, produce } from 'immer';

interface AIChatMessageError extends Api.AIChatMessage {
	error: string;
	chatRequest: Api.AIChatRequest;
}
interface PartialToolCall extends Api.AIToolCall {
	partial: Record<string, string>;
}

function isAIChatStreamTextResponse(sseData: Api.AIChatStreamResponse): sseData is Api.AIChatStreamMessageResponse {
	return Boolean((sseData as Api.AIChatStreamMessageResponse)?.message);
}

function isAIChatStreamToolCallResponse(sseData: Api.AIChatStreamResponse): sseData is Api.AIChatStreamToolResponse {
	return Boolean((sseData as Api.AIChatStreamToolResponse)?.toolCall);
}

export const CLIENT_MESSAGE_ID_PREFIX = 'client-message-id-';

export function syncAIStreamToRQCache({
	event,
	queryClient,
	queryKey,
	chatRequest,
}: {
	event: ServerSentEventMessage;
	queryClient: QueryClient;
	queryKey: QueryKey;
	chatRequest: Api.AIChatRequest;
}) {
	if (event.event === 'Error') {
		queryClient.setQueryData(queryKey, (messages: Api.AIChatMessage[]) => {
			const errorMessage: AIChatMessageError = {
				id: `${CLIENT_MESSAGE_ID_PREFIX}${crypto.randomUUID()}`,
				error: event.data,
				chatRequest,
			};
			return [...messages, errorMessage];
		});
		return;
	}
	if (event.event !== 'Response' && event.event !== 'ToolResponse') {
		return;
	}
	const aiChatStreamMessageResponse = JSON.parse(event.data) as Api.AIChatStreamResponse;
	if (isAIChatStreamTextResponse(aiChatStreamMessageResponse)) {
		queryClient.setQueryData(queryKey, (messages: Api.AIChatMessage[]) => {
			const aiTextMessage: Api.AITextContent = {
				_type: 'AITextContent',
				text: aiChatStreamMessageResponse.message.text,
			};
			return produce(messages ?? [], draftMessages => {
				const currentMessage = draftMessages.length > 0 ? draftMessages[draftMessages.length - 1] : undefined;
				if (currentMessage?.role !== Api.AIChatRole.Assistant) {
					draftMessages.push({
						id: `${CLIENT_MESSAGE_ID_PREFIX}${aiChatStreamMessageResponse.id}`,
						role: Api.AIChatRole.Assistant,
						contents: [aiTextMessage],
					});
					return draftMessages;
				}
				const lastMessageContent = currentMessage.contents[
					currentMessage.contents.length - 1
				] as Draft<Api.AITextContent>;
				if (lastMessageContent._type !== aiTextMessage._type) {
					currentMessage.contents.push(aiTextMessage);
					return draftMessages;
				}
				if (!aiChatStreamMessageResponse.isDelta) {
					currentMessage.contents.splice(currentMessage.contents.length - 1, 1, aiTextMessage);
					return draftMessages;
				}
				lastMessageContent.text += aiTextMessage.text;
				return draftMessages;
			});
		});
	}
	if (
		isAIChatStreamToolCallResponse(aiChatStreamMessageResponse) &&
		(aiChatStreamMessageResponse.toolCall.toolName === 'lev-contact' ||
			aiChatStreamMessageResponse.toolCall.toolName === 'lev-contacts' ||
			aiChatStreamMessageResponse.toolCall.toolName === 'lev-navigation' ||
			aiChatStreamMessageResponse.toolCall.toolName === 'lev-meeting-scheduling')
	) {
		if (aiChatStreamMessageResponse.isDelta) {
			return;
		}
		queryClient.setQueryData(queryKey, (messages: Api.AIChatMessage[]) => {
			const aiToolCallContent: Api.AIToolContent = {
				_type: 'AIToolContent',
				toolCall: aiChatStreamMessageResponse.toolCall,
			};
			return produce(messages ?? [], draftMessages => {
				const currentMessage = draftMessages.length > 0 ? draftMessages[draftMessages.length - 1] : undefined;
				if (currentMessage?.role !== Api.AIChatRole.Assistant) {
					draftMessages.push({
						id: `${CLIENT_MESSAGE_ID_PREFIX}${aiChatStreamMessageResponse.id}`,
						role: Api.AIChatRole.Assistant,
						contents: [aiToolCallContent],
					});
					return draftMessages;
				}
				currentMessage.contents.push(aiToolCallContent);
				return draftMessages;
			});
		});
		return;
	}
	if (isAIChatStreamToolCallResponse(aiChatStreamMessageResponse)) {
		if (aiChatStreamMessageResponse.isDelta && !aiChatStreamMessageResponse.toolCall?.propertyName) {
			return;
		}
		queryClient.setQueryData(queryKey, (messages: Api.AIChatMessage[]) => {
			const aiToolCallContent: Api.AIToolContent = {
				_type: 'AIToolContent',
				toolCall: {
					toolName: aiChatStreamMessageResponse.toolCall.toolName,
					value: '',
				} as PartialToolCall,
			};
			if (aiChatStreamMessageResponse.isDelta) {
				(aiToolCallContent.toolCall as PartialToolCall).partial = {
					[aiChatStreamMessageResponse.toolCall.propertyName]: aiChatStreamMessageResponse.toolCall.value,
				};
			} else {
				aiToolCallContent.toolCall = aiChatStreamMessageResponse.toolCall;
			}
			return produce(messages ?? [], draftMessages => {
				const currentMessage = draftMessages.length > 0 ? draftMessages[draftMessages.length - 1] : undefined;
				if (currentMessage?.role !== Api.AIChatRole.Assistant) {
					draftMessages.push({
						id: `${CLIENT_MESSAGE_ID_PREFIX}${aiChatStreamMessageResponse.id}`,
						role: Api.AIChatRole.Assistant,
						contents: [aiToolCallContent],
					});
					return draftMessages;
				}
				const lastMessageContent = currentMessage.contents[
					currentMessage.contents.length - 1
				] as Draft<Api.AIToolContent>;
				if (
					lastMessageContent._type !== aiToolCallContent._type ||
					!(lastMessageContent.toolCall as PartialToolCall)?.partial
				) {
					currentMessage.contents.push(aiToolCallContent);
					return draftMessages;
				}
				if (!aiChatStreamMessageResponse.isDelta) {
					currentMessage.contents.splice(currentMessage.contents.length - 1, 1, aiToolCallContent);
					return draftMessages;
				}
				(lastMessageContent.toolCall as PartialToolCall).partial[aiChatStreamMessageResponse.toolCall.propertyName] =
					((lastMessageContent.toolCall as PartialToolCall).partial[
						aiChatStreamMessageResponse.toolCall.propertyName
					] ?? '') + aiChatStreamMessageResponse.toolCall.value;
				return draftMessages;
			});
		});
	}
}
